/**
 * @fileoverview Floating selection action bar for Needs Review listings.
 * @module features/sku-alias/components/NeedsReviewSelectionActionBar
 */

import { AnimatePresence, motion } from 'framer-motion'
import { Check, Loader2 } from 'lucide-react'
import { ReactElement, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { LookupConfirmField } from '@/shared/components/LookupConfirmField'
import { searchCatalogProducts } from '@/features/sku-alias/api/needsReview'
import type { Variant } from '@/lib/variantService'
import type { UnmatchedListing } from '@/features/sku-alias/schemas/unmatchedListingSchema'

export interface NeedsReviewSelectionActionBarProps {
  /** Number of selected listings */
  count: number
  /** Whether any bulk action is currently executing */
  isBusy?: boolean
  /** Triggered when user wants to link selected listings to a product */
  onAssignBulk: (productId: number) => Promise<void>
  /** The first selected listing to use as a template for creation */
  templateListing?: UnmatchedListing | undefined
  /** Clears the current selection */
  onClearSelection: () => void
}

const MIN_SEARCH_LENGTH = 2
const MAX_VISIBLE_RESULTS = 6

/**
 * Floating selection bar displayed when listings are selected.
 *
 * @component
 */
export function NeedsReviewSelectionActionBar({
  count,
  isBusy = false,
  onAssignBulk,
  templateListing,
  onClearSelection,
}: NeedsReviewSelectionActionBarProps): ReactElement | null {
  const { t } = useTranslation('features-sku-alias')
  const navigate = useNavigate()

  const handleConfirm = useCallback(
    async (sku: Variant): Promise<void> => {
      if (isBusy) return
      await onAssignBulk(sku.id)
    },
    [isBusy, onAssignBulk]
  )

  const handleCreateNew = useCallback((): void => {
    if (!templateListing) return
    navigate('/dashboard/management/products/needs-review/create', { state: { listing: templateListing } })
  }, [navigate, templateListing])

  useEffect(() => {
    if (count === 0) return undefined

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClearSelection()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [count, onClearSelection])

  if (count === 0) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        role="region"
        aria-live="polite"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="fixed top-0 left-0 lg:left-20 right-0 z-40 border-b border-border bg-card shadow-md"
      >
        <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-4 px-4 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={onClearSelection}
              className="gap-2"
              aria-label={t('needsReview.selection.clear', { defaultValue: 'Clear selection' })}
            >
              {t('needsReview.selection.clear', { defaultValue: 'Clear selection' })}
              <Kbd>Esc</Kbd>
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('needsReview.selection.count', {
                count,
                defaultValue: '{{count}} selected',
              })}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="w-[320px]">
              <LookupConfirmField<Variant>
                fetchResults={searchCatalogProducts}
                onConfirm={handleConfirm}
                {...(templateListing ? { onCreateNew: handleCreateNew } : {})}
                createNewLabel={t('needsReview.table.actions.createNew')}
                renderSuggestion={({ item }) => (
                  <div className="flex flex-col gap-1">
                    <span
                      className="block max-w-[18rem] truncate font-medium"
                      title={item.productName ?? item.name}
                    >
                      {item.productName ?? item.name}
                    </span>
                    <span
                      className="block max-w-[18rem] truncate text-xs text-muted-foreground"
                      title={item.name}
                    >
                      {item.name}
                    </span>
                  </div>
                )}
                getItemLabel={item => item.productName ?? item.name ?? ''}
                placeholder={t('needsReview.table.actions.assignToExisting')}
                disabled={isBusy}
                minSearchLength={MIN_SEARCH_LENGTH}
                maxVisibleResults={MAX_VISIBLE_RESULTS}
                confirmAriaLabel={t('needsReview.table.actions.confirmMapping')}
                confirmIcon={
                  isBusy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />
                }
                queryKeyBase={['variant', 'search']}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default NeedsReviewSelectionActionBar
